import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Breadcrumb, Loading } from "../components/ui";
import { getAvatarUrl, USERS } from "../config/users";
import { UserAuth } from "../context/AuthContext";
import { useAllUsersPresence } from "../context/PresenceContext";

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const isOnline = lastSeen => {
  if (!lastSeen) return false;
  const lastSeenDate = lastSeen instanceof Date ? lastSeen : new Date(lastSeen);
  return Date.now() - lastSeenDate.getTime() < ONLINE_THRESHOLD_MS;
};

const formatLastSeen = lastSeen => {
  if (!lastSeen) return "Never";

  const now = new Date();
  const lastSeenDate = lastSeen instanceof Date ? lastSeen : new Date(lastSeen);
  const diffMs = now - lastSeenDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return lastSeenDate.toLocaleDateString();
};

const RoleBadge = ({ role }) => {
  const styles = {
    developer: "bg-primary/10 text-primary",
    user: "bg-base-content/5 text-base-content/60",
  };

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${styles[role] || styles.user}`}>
      {role || "user"}
    </span>
  );
};

const UserAvatar = ({ user, size = "md", googleAvatar = null }) => {
  const sizeClasses = {
    sm: "w-6 h-6 text-xs",
    md: "w-8 h-8 text-sm",
    lg: "w-10 h-10 text-base",
  };

  // Use config avatar first, then Google avatar from presence data
  const avatarUrl = getAvatarUrl(user.email) || googleAvatar;

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={user.name}
        className={`${sizeClasses[size]} rounded-full object-cover`}
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0`}>
      <span className="text-primary font-medium">
        {user.name?.charAt(0)?.toUpperCase() || "?"}
      </span>
    </div>
  );
};

function AdminUsers() {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const { usersPresence, loading } = useAllUsersPresence();

  useEffect(() => {
    if (user === null) {
      navigate("/signin");
    }
  }, [user, navigate]);

  if (!user?.email) {
    return null;
  }

  if (loading) {
    return <Loading size="lg" message="Loading users..." fullScreen />;
  }

  // Merge config users with presence data
  const mergedUsers = USERS.map(configUser => {
    const presence = usersPresence.find(p => p.email === configUser.email);
    return {
      ...configUser,
      lastSeen: presence?.lastSeen || null,
      currentProject: presence?.currentProject || null,
      online: isOnline(presence?.lastSeen),
      googleAvatar: presence?.avatar || null, // Google avatar from login
    };
  });

  // Sort: role (developers first), then online, then by last seen
  const sortedUsers = mergedUsers.sort((a, b) => {
    // Developers first
    if (a.role === "developer" && b.role !== "developer") return -1;
    if (a.role !== "developer" && b.role === "developer") return 1;
    // Then online users
    if (a.online && !b.online) return -1;
    if (!a.online && b.online) return 1;
    // Then by last seen
    if (a.lastSeen && !b.lastSeen) return -1;
    if (!a.lastSeen && b.lastSeen) return 1;
    if (a.lastSeen && b.lastSeen) return b.lastSeen - a.lastSeen;
    return a.name.localeCompare(b.name);
  });

  // Stats
  const onlineCount = sortedUsers.filter(u => u.online).length;

  return (
    <div className="container mx-auto px-4 py-6">
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Users" },
        ]}
        className="mb-4"
      />

      {/* Header Card */}
      <div className="bg-base-100 rounded-lg p-4 sm:p-5 mb-6 border border-base-content/10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-base-content">Users</h1>
            <p className="text-sm text-base-content/50 mt-1">{sortedUsers.length} users</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-base-content/5 rounded-lg">
            <FontAwesomeIcon icon={faCircle} className="text-success text-[8px]" />
            <span className="text-sm font-medium text-base-content">{onlineCount}</span>
            <span className="text-sm text-base-content/50">online now</span>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-base-100 rounded-lg border border-base-content/10 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-base-content/10 bg-base-content/[0.02]">
              <th className="text-left text-xs font-medium text-base-content/50 uppercase tracking-wider px-4 py-3">
                User
              </th>
              <th className="text-left text-xs font-medium text-base-content/50 uppercase tracking-wider px-4 py-3">
                Role
              </th>
              <th className="text-left text-xs font-medium text-base-content/50 uppercase tracking-wider px-4 py-3">
                Currently Viewing
              </th>
              <th className="text-left text-xs font-medium text-base-content/50 uppercase tracking-wider px-4 py-3">
                Last Active
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-content/5">
            {sortedUsers.map(u => (
              <tr key={u.email} className="hover:bg-base-content/[0.02] transition-colors">
                {/* User info with online indicator */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <UserAvatar user={u} googleAvatar={u.googleAvatar} />
                      {u.online && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-base-100" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-base-content truncate">
                        {u.name}
                      </div>
                      <div className="text-xs text-base-content/40 truncate">{u.email}</div>
                    </div>
                  </div>
                </td>

                {/* Role */}
                <td className="px-4 py-3">
                  <RoleBadge role={u.role} />
                </td>

                {/* Currently Viewing */}
                <td className="px-4 py-3">
                  {u.currentProject ? (
                    <Link
                      to={`/projects/${u.currentProject.id}`}
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full hover:bg-primary/20 transition-colors truncate max-w-[200px]"
                      title={u.currentProject.name}
                    >
                      <FontAwesomeIcon icon={faCircle} className="text-[6px]" />
                      <span className="truncate">{u.currentProject.name}</span>
                    </Link>
                  ) : (
                    <span className="text-xs text-base-content/30">—</span>
                  )}
                </td>

                {/* Last Active */}
                <td className="px-4 py-3">
                  <span className={`text-xs ${u.online ? "text-success font-medium" : "text-base-content/50"}`}>
                    {u.online ? "Online" : formatLastSeen(u.lastSeen)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminUsers;
